import _extends from "@babel/runtime/helpers/extends";
import React, { Suspense, useCallback, useEffect } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ClearIcon from "@mui/icons-material/Clear";
import loadable from "@loadable/component";
import classNames from "classnames";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatable from "react-select/async-creatable";
import Creatable from "react-select/creatable";
import Button from "../Button";
import { isWindowAvailable } from "../Services/windowService";
import TextInput from "../TextInput";
import MenuList from "./MenuList";
import MultiValue from "./MultiValue";
import { MultiValueLabel } from "./MultiValueLabel";
import Option from "./Option";
import Selection from "./Selection";
import useStyles from "./styles";
const Droppable = loadable(() => import("react-drag-and-drop/lib/Droppable"));
const sortValuesByLabel = values => {
  values.sort((a, b) => {
    let aLabel, bLabel;
    if (isNaN(a.label)) {
      aLabel = a.label.toLowerCase();
    } else {
      aLabel = parseFloat(a.label);
    }
    if (isNaN(b.label)) {
      bLabel = b.label.toLowerCase();
    } else {
      bLabel = parseFloat(b.label);
    }
    if (aLabel < bLabel) {
      return -1;
    }
    if (aLabel > bLabel) {
      return 1;
    }
    return 0;
  });
};
const Autocomplete = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    id,
    disabled,
    label,
    loadOptions,
    multiSelect = false,
    options,
    placeholder = "Select or type to filters...",
    value,
    allowOptionCreation,
    className,
    openMenuWhenClicked,
    sortSelectedOptions,
    sortMenuOptions,
    menuCreationText,
    defaultValue,
    isInDialog,
    variant = "outlined",
    noOptionsMessage,
    inputProps,
    styles,
    closeMenuOnSelect,
    isClearable,
    enableDroppable,
    readOnly,
    onChange,
    inputThreshold = 3,
    maxChips = 3,
    cacheOptions,
    defaultOptions,
    fullWidth = true,
    ...otherProps
  } = props;
  const classes = useStyles(props);
  const [portalElement] = React.useState(isWindowAvailable() ? document.createElement("div") : null);
  const addPortalElement = useCallback(() => {
    if (!isWindowAvailable() || portalElement == null) {
      return;
    }
    if (isInDialog && !document.body.contains(portalElement)) {
      document.body.appendChild(portalElement);
    }
  }, [portalElement, isInDialog]);
  const removePortalElement = useCallback(() => {
    if (!isWindowAvailable() || portalElement == null) {
      return;
    }
    if (document.body.contains(portalElement)) {
      document.body.removeChild(portalElement);
    }
  }, [portalElement]);
  useEffect(() => {
    if (isInDialog) {
      addPortalElement();
    } else {
      removePortalElement();
    }

    // returned function will be called on component unmount
    return () => {
      removePortalElement();
    };
  }, [isInDialog, addPortalElement, removePortalElement]);
  const triggerLoadOptions = (input, callback) => {
    const threshold = Number(inputThreshold);
    if (input.length >= threshold && loadOptions) {
      return loadOptions(input, callback);
    } else {
      return Promise.resolve([]);
    }
  };
  const getValue = () => {
    if ((typeof value === "string" || typeof value === "number") && options) {
      return options.find(option => option.name === value);
    }
    const selectedOptions = value;
    if (multiSelect) {
      if (sortSelectedOptions) {
        sortValuesByLabel(selectedOptions);
      }
    }
    return selectedOptions;
  };
  const menuCreationTextHelper = (templateString, text) => templateString.replace("{{label}}", text);
  const handleDrop = object => {
    if (enableDroppable && onChange && options) onChange(options.filter(option => option.label === object.label));
  };
  const handleChange = onChange ? (value, item) => {
    if (item.option && item.option.value === "Select All") {
      //	Checks to see if the options are grouped options if so then it returns a joined value list for Select all.
      //	If the options are not grouped then it returns the normal options array for Select all.
      const isGroupedOptions = menuOptions?.some(array => array.options && array.options.length > 0);
      let options = menuOptions;
      if (isGroupedOptions) {
        options = menuOptions?.flatMap(item => item.options).filter(item => item !== undefined);
      }
      if (options?.length === value.length - 1) {
        if (multiSelect) {
          onChange([]);
        } else {
          onChange(undefined);
        }
      } else {
        if (multiSelect) {
          onChange(options);
        } else {
          onChange(options[0] || undefined);
        }
      }
    } else {
      onChange(value || []);
    }
  } : undefined;
  const ClearIndicator = ({
    innerProps
  }) => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, _extends({
      className: classes.clearButton,
      type: "icon",
      title: Array.isArray(value) && value.length > 1 ? "Clear Values" : "Clear Value"
    }, innerProps), /*#__PURE__*/React.createElement(ClearIcon, {
      fontSize: "small"
    })));
  };
  const SelectComponent = allowOptionCreation && loadOptions ? AsyncCreatable : allowOptionCreation ? Creatable : loadOptions ? AsyncSelect : Select;
  const renderedNoOptionsMessage = typeof noOptionsMessage !== "undefined" ? noOptionsMessage : allowOptionCreation ? "No options. Start typing to create a new one" : "No options";
  const selectedValues = getValue();

  // used for the react-select components
  const selectedOptions = value;
  const menuOptions = options || [];
  if (sortSelectedOptions && selectedOptions && Array.isArray(selectedOptions) && selectedOptions.length > 1) {
    sortValuesByLabel(selectedOptions);
  }
  if (sortMenuOptions && menuOptions && menuOptions.length > 1) {
    sortValuesByLabel(menuOptions);
  }
  const renderedInputProps = {
    components: {
      ClearIndicator,
      Option,
      MultiValueLabel,
      MenuList,
      MultiValue
    },
    arrowRenderer: arrowProps => {
      return arrowProps.isOpen ? /*#__PURE__*/React.createElement(ArrowDropUpIcon, null) : /*#__PURE__*/React.createElement(ArrowDropDownIcon, null);
    },
    clearRenderer: () => /*#__PURE__*/React.createElement(ClearIcon, null),
    id: id,
    isDisabled: disabled || readOnly,
    isMulti: multiSelect,
    loadOptions: loadOptions ? triggerLoadOptions : undefined,
    options: multiSelect && menuOptions?.length > 0 ? [{
      label: "Select All",
      value: "Select All"
    }, ...new Set(menuOptions)] : menuOptions || [],
    optionComponent: Option,
    valueComponent: Selection,
    styles: styles,
    isClearable: isClearable,
    enableDroppable: enableDroppable,
    defaultValue: defaultValue,
    openMenuOnClick: openMenuWhenClicked,
    placeholder: placeholder,
    closeMenuOnSelect: closeMenuOnSelect === undefined ? multiSelect ? false : true : closeMenuOnSelect,
    hideSelectedOptions: false,
    promptTextCreator: menuCreationText ? inputValue => menuCreationTextHelper(menuCreationText, inputValue) : inputValue => inputValue,
    formatCreateLabel: menuCreationText ? inputValue => menuCreationTextHelper(menuCreationText, inputValue) : undefined,
    value: selectedValues,
    classNamePrefix: variant === "compact" ? classes.dropdownInputWrapper + " " + classes.dropdownInputWrapperCompact + " prmbl-autocomplete-compact prmbl-autocomplete" : variant === "inline" ? classes.dropdownInputWrapper + " " + classes.dropdownInputWrapperInline + " prmbl-autocomplete" : classes.dropdownInputWrapper + " prmbl-autocomplete",
    noOptionsMessage: typeof noOptionsMessage === "function" ? noOptionsMessage : () => renderedNoOptionsMessage,
    maxChips: maxChips,
    cacheOptions,
    defaultOptions,
    "aria-labelledby": id ? `${id}-label` : undefined,
    "aria-label": id ? undefined : label,
    "aria-description": placeholder,
    ...inputProps,
    className: classNames(classes.dropdownInputWrapper, "prmbl-dropdownInputWrapper", inputProps?.className)
  };
  if (isInDialog) {
    renderedInputProps["minMenuHeight"] = 100;
    renderedInputProps["menuPosition"] = "fixed";
    if (portalElement != null) {
      renderedInputProps["menuPortalTarget"] = portalElement;
      portalElement.className = classes.portalElement;
      portalElement.setAttribute("data-testid", `${id ? id + "-" : ""}autocomplete-menu-portal`);
    }
  }
  const buildDrop = children => {
    if (enableDroppable) {
      return /*#__PURE__*/React.createElement(Suspense, {
        fallback: children
      }, /*#__PURE__*/React.createElement(Droppable, {
        types: ["label"],
        onDrop: handleDrop
      }, children));
    }
    return children;
  };
  return buildDrop(/*#__PURE__*/React.createElement(TextInput, _extends({}, otherProps, {
    ref: ref,
    id: id,
    label: label,
    value: value ? value : "",
    fullWidth: fullWidth,
    disabled: disabled || readOnly,
    className: classNames("prmbl-autocomplete", className, classes.autoCompleteWrapper, {
      [classes.disabled]: disabled,
      [classes.readOnly]: readOnly
    }),
    InputLabelProps: {
      shrink: true,
      classes: {
        shrink: classes.autoCompleteLabel
      }
    },
    placeholder: placeholder,
    onChange: handleChange,
    inputComponent: SelectComponent,
    variant: variant,
    inputProps: renderedInputProps,
    "data-testid": `prmbl-autocomplete-input-${id ?? ""}`
  })));
});
Autocomplete.defaultProps = {
  inputThreshold: 3,
  variant: "outlined",
  fullWidth: true,
  placeholder: "Select or type to filter...",
  maxChips: 3,
  multiSelect: false
};
Autocomplete.displayName = "Autocomplete";
export default Autocomplete;